// Live board (plan §7): who is in, expected, out and with whom, absent; flagged handovers; the
// site's kiosks and phones. GET board, then the site WebSocket tells us when to refetch.
import { VavrNews } from "../support/News.tsx";
import { useMemo, useState } from "react";
import { Badge, Banner, Button, Card, EmptyState, Select, Table, TextField, initials, type BadgeTone } from "@vavr/ui";
import { api } from "../shared/index.ts";
import { useAction } from "./data.tsx";
import { eventLabel, FLAG_LABEL, Link, fullName, time } from "../shared/index.ts";
import { useConsole, useGet } from "./data.tsx";
import { LoadError, Loading, Toolbar, useNames } from "./common.tsx";
import { useLiveState } from "./live.tsx";
import { eventTime } from "./reports.ts";
import { statusLine, type BoardChild } from "../teacher/board.ts";

interface BoardData { reunification?: { since: number; place: string | null } | null; children: BoardChild[]; flagged: any[]; head: { seq: number }; devices: { id: string; kind: string; name: string; last_seen_at: number | null; outbox_depth: number | null; battery: number | null }[] }

export const STATE: Record<string, { label: string; tone: BadgeTone }> = {
  in: { label: "In", tone: "success" }, expected: { label: "Expected", tone: "neutral" }, out: { label: "Gone home", tone: "info" }, absent: { label: "Absent", tone: "warning" },
};

export function Board() {
  const { base, site, can } = useConsole();
  const board = useGet<BoardData>(`${base}/board`);
  const { names } = useNames();
  // The console shell holds the site socket and refetches this page on every change (live.tsx).
  const live = useLiveState();
  const [q, setQ] = useState("");
  const [room, setRoom] = useState("all");

  const counts = useMemo(() => {
    const c = { in: 0, expected: 0, out: 0, absent: 0 } as Record<string, number>;
    for (const ch of board.data?.children ?? []) c[ch.state] = (c[ch.state] ?? 0) + 1;
    return c;
  }, [board.data]);

  if (board.loading) return <Loading label="Loading the board" />;
  if (board.error || !board.data) return <LoadError error={board.error ?? new Error("No data")} retry={board.reload} />;
  const query = q.trim().toLowerCase();
  const kids = board.data.children.filter((c) => (!query || fullName(c).toLowerCase().includes(query)) && (room === "all" || (room === "unassigned" ? !c.room_id : c.room_id === room)));
  const rooms = [...new Set(board.data.children.map((c) => c.room_id).filter((id): id is string => !!id))].map((id) => ({ value: id, label: names.room(id) || "Unnamed room" })).sort((a, b) => a.label.localeCompare(b.label));
  const details = (c: BoardChild) => {
    const e = c.last_event;
    if (e?.type === "pickup" && !e.adult && e.adult_id && !e.home_alone && !e.sibling && !e.reunification) return `Left with ${names.adult(e.adult_id)} at ${time(e.server_ts, site.tz)}`;
    return statusLine(c, site.tz) || "No event recorded today";
  };
  const descriptions: Record<string, string> = { in: "Currently checked in", expected: "No arrival recorded today", out: "Released from care", absent: "Marked absent today" };
  const empty: Record<string, string> = { in: "No children checked in", expected: "No children awaiting arrival", out: "No departures recorded", absent: "No absences recorded" };
  const date = new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: site.tz }).format(Date.now());

  return (
    <div className="pmu-stack pmu-c-dashboard">
      <header className="pmu-c-dashboard__intro">
        <div><p className="pmu-c-dashboard__date">{date} · {site.tz.replaceAll("_", " ")}</p><h2>Today at {site.name}</h2><p className="pmu-muted">Attendance, handovers and device reports for your visible roster.</p></div>
        <div className="pmu-c-dashboard__connection"><Badge tone={live === "live" ? "success" : "warning"} icon={live === "live" ? "cloud" : "cloud-off"} aria-live="polite">{live === "live" ? "Live updates" : live === "off" ? "Not live" : "Connecting…"}</Badge><Button size="sm" variant="secondary" onClick={board.reload}>Refresh board</Button></div>
      </header>
      <VavrNews />
      {live !== "live" && <Banner tone="warning" title="Live updates are unavailable">These are the last loaded records. Refresh the board to check for changes.</Banner>}
      {board.data.reunification && <Reunification base={base} state={board.data.reunification} canStart={can("site.configure")} stillHere={counts.in ?? 0} tz={site.tz} />}

      <section className="pmu-c-stats" aria-label="Today">
        {(["in", "expected", "out", "absent"] as const).map((s) => (
          <div key={s} className={`pmu-c-stat pmu-c-stat--${s}`}>
            <span className="pmu-c-stat__value">{counts[s] ?? 0}</span>
            <span className="pmu-c-stat__label">{STATE[s]!.label}</span>
            <span className="pmu-c-stat__hint">{descriptions[s]}</span>
          </div>
        ))}
      </section>

      {board.data.flagged.length > 0 ? (
        <Card className="pmu-c-dashboard__flags" title={`Flagged today (${board.data.flagged.length})`} description="Handovers the server checked and flagged. Open one to see the photo and signature." flush>
          <Table
            caption="Flagged handovers" hideCaption rowKey={(e: any) => String(e.seq)} rows={board.data.flagged}
            columns={[
              { key: "t", header: "Time", width: "6rem", render: (e: any) => <Link className="pmu-link" href={`/console/events/${e.id}`}>{time(eventTime(e), site.tz)}</Link> },
              { key: "c", header: "Child", rowHeader: true, render: (e: any) => names.child(e.child_id) },
              { key: "y", header: "What", render: (e: any) => eventLabel(e.type) },
              { key: "a", header: "Adult", render: (e: any) => (e.adult_id ? names.adult(e.adult_id) : e.pre?.claimed_name ?? "Unlisted adult") },
              { key: "f", header: "Flags", render: (e: any) => <span className="pmu-row">{e.server_flags.map((f: string) => <Badge key={f} tone={f === "restriction_hit" ? "restriction" : "warning"}>{FLAG_LABEL[f] ?? f}</Badge>)}</span> },
            ]}
          />
        </Card>
      ) : <div className="pmu-c-dashboard__clear"><Badge tone="success" icon="check">No flagged handovers today</Badge><span className="pmu-muted">Based on records received by the server.</span></div>}

      <section className="pmu-c-dashboard__roster" aria-label="Children on the board">
      <Toolbar>
        <TextField label="Find a child" placeholder="Search by name  ( / )" value={q} onChange={(e) => setQ(e.target.value)} data-search="" type="search" />
        <Select label="Room" value={room} onValueChange={(v) => setRoom(v ?? "all")} options={[{ value: "all", label: "All rooms" }, ...rooms, { value: "unassigned", label: "No room assigned" }]} />
        {(query || room !== "all") && <Button variant="ghost" size="sm" onClick={() => { setQ(""); setRoom("all"); }}>Clear filters</Button>}
      </Toolbar>
      <p className="pmu-c-dashboard__results" role="status">Showing {kids.length} of {board.data.children.length} children. Summary totals cover your full board.</p>
      {board.data.children.length === 0 ? <EmptyState icon="child" title="Your board is ready for children" description="Add children or import your roster to see attendance here." action={<Link className="pmu-link" href="/console/children">Open Children</Link>} />
        : kids.length === 0 ? <EmptyState icon="search" title="No children match these filters" description="Try another name or choose a different room." /> : <div className="pmu-c-board">
        {(["in", "expected", "out", "absent"] as const).map((s) => {
          const list = kids.filter((c) => c.state === s);
          return (
            <Card key={s} title={`${STATE[s]!.label} (${list.length})`} className={`pmu-c-board__col pmu-c-board__col--${s}`} description={descriptions[s]} flush>
              <ul className="pmu-c-kids">
                {list.length === 0 && <li className="pmu-muted pmu-c-kids__empty">{empty[s]}{query || room !== "all" ? " in this view." : "."}</li>}
                {list.map((c) => (
                  <li key={c.id}>
                    <KidRow child={c} room={names.room(c.room_id) || "No room assigned"} detail={details(c)} flags={c.last_event?.server_flags ?? []} />
                  </li>
                ))}
              </ul>
            </Card>
          );
        })}
      </div>}
      </section>

      {can("device.enrol") && (
        <Card title="Kiosks and phones" description="Last reported by each device. Waiting records have not synced yet." actions={<Link className="pmu-link" href="/console/devices">Manage devices</Link>} flush>
          <Table
            caption="Active devices" hideCaption rowKey={(d) => d.id} rows={board.data.devices}
            empty="No devices are enrolled. Add one under Devices."
            columns={[
              { key: "name", header: "Device", rowHeader: true },
              { key: "kind", header: "Kind", render: (d) => (d.kind === "kiosk" ? "Kiosk" : "Phone") },
              { key: "seen", header: "Last seen", render: (d) => <LastSeen ts={d.last_seen_at} tz={site.tz} /> },
              { key: "outbox", header: "Waiting to send", align: "end", render: (d) => (d.outbox_depth == null ? "Not reported" : d.outbox_depth > 0 ? <Badge tone="warning">{d.outbox_depth} records</Badge> : "All sent") },
              { key: "battery", header: "Battery", align: "end", render: (d) => (d.battery == null ? "—" : <span className={d.battery < 20 ? "pmu-c-warn" : undefined}>{d.battery}%</span>) },
            ]}
          />
        </Card>
      )}
      {!board.data.reunification && <Reunification base={base} state={null} canStart={can("site.configure")} stillHere={counts.in ?? 0} tz={site.tz} />}
    </div>
  );
}

function KidRow({ child, room, detail, flags }: { child: BoardChild; room: string; detail?: string; flags: string[] }) {
  // Initials, not photos: a board of 400 children should not fetch 400 images. Photos are on the child's page.
  return (
    <Link href={`/console/children/${child.id}`} className="pmu-c-kid">
      <span className="pmu-c-kid__photo" aria-hidden="true">{initials(fullName(child))}</span>
      <span className="pmu-c-kid__text">
        <span className="pmu-c-kid__name">{fullName(child)}</span>
        <span className="pmu-c-kid__room">{room}</span>
        {child.state === "in" && <span className="pmu-c-kid__location">{child.custodian === "door" ? "Called to the door" : child.custodian === "after_school" ? "At after-school" : child.custodian === "room" ? "In class" : "Checked in"}</span>}
        <span className="pmu-c-kid__detail">{detail}</span>
        {child.dismissal_plan && <span className="pmu-c-kid__detail">Usual plan: {child.dismissal_plan}</span>}
        {flags.length > 0 && <span className="pmu-row pmu-c-flags">{flags.map((f) => <Badge key={f} tone={f === "restriction_hit" ? "restriction" : "warning"}>{FLAG_LABEL[f] ?? f}</Badge>)}</span>}
      </span>
    </Link>
  );
}

export function LastSeen({ ts, tz }: { ts: number | null; tz: string }) {
  if (!ts) return <span className="pmu-muted">Not reported</span>;
  const mins = Math.max(0, Math.floor((Date.now() - ts) / 60_000));
  const label = mins < 1 ? "Just now" : mins < 60 ? `${mins} min ago` : new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: tz }).format(ts);
  return mins > 15 ? <Badge tone="warning">{label}</Badge> : <span>{label}</span>;
}

/**
 * Emergency reunification (school rules, U11): the office starts it with the place parents come to;
 * staff release children there to approved adults, and this banner counts who is still here.
 */
function Reunification({ base, state, canStart, stillHere, tz }: { base: string; state: { since: number; place: string | null } | null; canStart: boolean; stillHere: number; tz: string }) {
  const [place, setPlace] = useState("");
  const act = useAction();
  if (state) {
    return (
      <Banner tone="danger" title={`Reunification at ${state.place ?? "the meeting point"} since ${time(state.since, tz)}`}
        actions={canStart ? <Button size="sm" variant="secondary" loading={act.busy} onClick={() => void act.run(() => api("POST", `${base}/reunification`, { active: false }), { success: "Reunification ended.", invalidate: [`${base}/board`] })}>End reunification</Button> : undefined}>
        {stillHere} {stillHere === 1 ? "child is" : "children are"} still to be released. Staff release each one to an approved adult in the staff app; parents and the door tablet see where to go.
      </Banner>
    );
  }
  if (!canStart) return null;
  return (
    <details className="pmu-c-reunify">
      <summary>Emergency: start reunification</summary>
      <form className="pmu-row" onSubmit={(e) => { e.preventDefault(); if (place.trim()) void act.run(() => api("POST", `${base}/reunification`, { active: true, place }), { success: "Reunification started. Every app shows it.", invalidate: [`${base}/board`] }); }}>
        <TextField label="Where parents collect their children" value={place} maxLength={120} onChange={(e) => setPlace(e.target.value)} placeholder="Community center, 12 Main St" />
        <Button type="submit" variant="danger" loading={act.busy} disabled={!place.trim()}>Start</Button>
        {act.error && <Banner tone="danger" title={act.error} />}
      </form>
    </details>
  );
}
